import { AfterContentInit, ContentChild, Directive, OnDestroy, SkipSelf } from "@angular/core";
import { isEmpty } from "lodash";
import { Subscription } from "rxjs";
import { distinctUntilChanged, takeUntil } from "rxjs/operators";

import { CommonFormManagerDirective } from "../../form-manager.directive";
import { ToggleFieldGroupComponent } from "../field-toggle-group.component";
import { NoneControlDirective } from "../none-control.directive";
import { ToggleControlComponent } from "../toggle-control.component";
import { FieldToggleManager } from "../toggle-manager.service";

@Directive({
    selector: "common-field-toggle-group[multi-control-disabled-fields-strategy]",
    providers: [FieldToggleManager],
})
export class MultiControlDisabledFieldsStrategyDirective implements OnDestroy, AfterContentInit {
    @ContentChild(NoneControlDirective, { read: ToggleControlComponent, static: false })
    public noneControl: ToggleControlComponent;

    private state$: Subscription;
    private noneControlState$: Subscription;
    private toggleState$: Subscription[];

    constructor(@SkipSelf() private formManager: CommonFormManagerDirective,
                private hostRef: ToggleFieldGroupComponent,
                private toggleManager: FieldToggleManager) {
    }

    public ngAfterContentInit(): void {
        this.toggleManager.state$.pipe(takeUntil(this.hostRef.toggleFieldGroupTouched$)).subscribe((state) => {
            this.initializeToggleControls();
            this.initializeNoneControl();
        });
        this.hostRef.toggleFieldGroupTouched$.subscribe(() => {
            this.hostRef.toggleControls.forEach((toggleControl) => {
                toggleControl.toggleTouched = true;
            });

            this.state$ = this.toggleManager.state$.pipe(
                distinctUntilChanged(this.toggleManager.distinctUntilChangedFactory(this.allFieldProps)))
                .subscribe((state) => {
                    this.disableToggleControlsIfNeeded();
                    this.handleNoneControl();
                    this.forceApplySaneState(state);
                    this.setToggleFields();
                });

            this.noneControlState$ = this.noneControl.toggleState$.subscribe((state) => {
                this.hostRef.toggleFields.forEach((field) => field.isDisabled = state);
            });

            this.toggleState$ = this.hostRef.toggleControls.map((toggleControl) => {
                return toggleControl.toggleState$.subscribe((toggleState) => {
                    this.disableSiblingControls(toggleControl, toggleState);
                });
            });

        });
    }

    public ngOnDestroy(): void {
        if (this.noneControlState$) {
            this.noneControlState$.unsubscribe();
        }
        if (this.state$) {
            this.state$.unsubscribe();
        }
        if (this.toggleState$) {
            this.toggleState$.forEach((toggleState$) => toggleState$.unsubscribe());
        }
    }

    public disableSiblingControls(thisControl, state): void {
        if (!this.formManager.formIsBeingInitialized && this.toggleManager.toggleFieldsAreEmpty) {
            this.hostRef.toggleControls
                .filter((toggleControl) => toggleControl !== thisControl)
                .map((siblingControl) => {
                    siblingControl.disableToggle = state;
                });
        }
    }

    public disableToggleControlsIfNeeded(): void {
        this.hostRef.toggleControls.forEach((toggleControl) => {
            toggleControl.disableToggle = (this.dataIsPresent || this.noneControl.toggleState);
        });
    }

    public setToggleFields(): void {
        if (!this.toggleControlFieldsAreEmpty || this.noneControl.toggleState) {
            this.hostRef.toggleFields.forEach((field) => field.isDisabled = true);
        } else if (!this.toggleManager.toggleFieldsAreEmpty || (this.toggleControlFieldsAreEmpty && !this.noneControl.toggleState)) {
            this.hostRef.toggleFields.forEach((field) => field.isDisabled = false);
        }
    }

    public forceApplySaneState(state): void {
        this.hostRef.toggleControls.map((toggleControl) => {
            if (!!toggleControl.toggleState && !!toggleControl.disableToggle) {
                toggleControl.disableToggle = false;
            }
        });
    }

    public initializeToggleControls(): void {
        this.hostRef.toggleControls
            .filter((toggleControl) => toggleControl !== this.noneControl)
            .forEach((toggleControl) => {
                if (this.formManager.isEditForm) {
                    this.hostRef.toggleFields.forEach((toggleField) => toggleField.isDisabled = toggleControl.toggleState);
                    toggleControl.disableToggle = !toggleControl.toggleState;
                    if (this.noneControl.toggleState) {
                        this.noneControl.onToggleStateChange(!toggleControl.toggleState);
                    }
                }
            });
    }

    public initializeNoneControl(): void {
        if (!!this.noneControl && this.formManager.isEditForm) {
            if (isEmpty(this.hostRef.toggleControls.filter((tc) => tc.toggleState))) {
                this.noneControl.onToggleStateChange(this.dataIsEmpty);
                this.disableSiblingControls(this.noneControl, this.noneControl.toggleState);
            } else if (this.noneControl.toggleState && this.dataIsEmpty) {
                this.hostRef.toggleFields.forEach((toggleField) => toggleField.isDisabled = this.noneControl.toggleState);
            } else if (this.dataIsPresent) {
                this.noneControl.onToggleStateChange(false);
            }
            this.noneControl.disableToggle = !this.noneControl.toggleState;
        }
    }

    public handleNoneControl(): void {
        if (!!this.noneControl && (this.dataIsPresent || this.toggleControlsFieldsAreNotEmpty)) {
            this.noneControl.onToggleStateChange(false);
            this.noneControl.disableToggle = true;
        }
        this.hostRef.toggleFields.forEach((toggleField) => toggleField.isDisabled = this.noneControl.toggleState);
    }

    public get allFieldProps(): string[] {
        return [...this.toggleManager.allToggleFieldProps, ...this.allControlFieldProps];
    }

    public get allControlFieldProps(): string[] {
        return this.hostRef.toggleControls
            .filter((toggleControl) => !!toggleControl.toggleIsFormField && !!toggleControl.toggleFieldProp)
            .map((formFieldControl) => formFieldControl.toggleFieldProp);
    }

    public allControlFieldValues(allControlFieldProps): any[] {
        return this.toggleManager.mapReduceFieldPropsToValues(allControlFieldProps);
    }

    public get toggleControlFieldsExist(): boolean {
        return this.allControlFieldProps.length > 0;
    }

    public get toggleControlFieldsAreEmpty(): boolean {
        return this.allControlFieldValues(this.allControlFieldProps).every((fieldValue) => !fieldValue);
    }

    public get toggleControlsFieldsAreNotEmpty(): boolean {
        return !!this.toggleControlFieldsExist && !this.toggleControlFieldsAreEmpty;
    }

    public disableToggleFieldsWhenControlsToggled(toggleControl, state): void {
        if (!toggleControl.disableToggle) {
            this.hostRef.toggleFields.forEach((toggleField) => toggleField.isDisabled = state);
        }
    }

    public get dataIsEmpty(): boolean {
        return this.toggleManager.allFieldsAreEmpty(this.allFieldProps);
    }

    public get dataIsPresent(): boolean {
        return !this.dataIsEmpty;
    }
}
